import { ProductKey } from '@atlassiansox/bxpkit-core';
import _ from 'lodash';
import {
  GetProductKeysOptionsType,
  IncludesProductOptionsType,
  ProductKeysType
} from '../../../../../model';
import {
  BITBUCKET,
  EDITION_LABEL_ARRAY,
  FREE_EDITION_LABEL_KEY,
  OPSGENIE,
  PREMIUM_EDITION_LABEL_KEY,
  PRODUCT_NAMES,
  STANDARD_EDITION_LABEL_KEY
} from './constants';

/**
 * Transform productData into to Product-typed shape
 */

export const getProductKeys = ({
  productData,
  queryParameters,
  productOverride,
  bundleOverride,
  editionOverride
}: GetProductKeysOptionsType): ProductKeysType[] => {
  // Check for JSM, overwrite to JSD productKey
  if (queryParameters?.product?.includes('jira-service-management.ondemand')) {
    queryParameters.product = queryParameters.product
      .split(',')
      .map((product: string) => {
        return product === 'jira-service-management.ondemand'
          ? ProductKey.JIRA_SERVICE_DESK
          : product;
      })
      .join();
  }

  let productOrBundleData = queryParameters.hasOwnProperty('product')
    ? _.find(productData, { bundleProductKeys: `${queryParameters.product}` })
    : _.get(
        productData,
        `${queryParameters.bundle}`.split('.')[0],
        _.get(productData, 'default')
      );

  if (productOrBundleData === undefined) {
    productOrBundleData = productData.default;
  }
  if (productOverride && productOverride.length > 0) {
    productOrBundleData = _.find(productData, {
      bundleProductKeys: productOverride
    });
  }
  if (bundleOverride && bundleOverride.length > 0) {
    productOrBundleData = _.get(
      productData,
      `${bundleOverride}`.split('.')[0],
      _.get(productData, 'default')
    );
  }

  const editionQueryParmSupplied = queryParameters.hasOwnProperty('edition');
  const migrationSourceUuidParmSupplied = queryParameters.hasOwnProperty(
    'migrationSourceUuid'
  );

  let edition: string;
  if (queryParameters.hasOwnProperty('edition')) {
    edition = EDITION_LABEL_ARRAY.some(
      (label) => label.toLowerCase() === queryParameters.edition
    )
      ? queryParameters.edition
      : FREE_EDITION_LABEL_KEY.toLowerCase();
  } else {
    edition = FREE_EDITION_LABEL_KEY.toLowerCase();
  }

  if (editionOverride && editionOverride.length > 0) {
    edition = editionOverride.toLowerCase();
  }

  /* Extended Cloud Trial License (ECTL) instances should be Standard Edition
     if no edition query parm supplied*/
  if (migrationSourceUuidParmSupplied && !editionQueryParmSupplied) {
    edition = STANDARD_EDITION_LABEL_KEY.toLowerCase();
  } else if (
    migrationSourceUuidParmSupplied &&
    editionQueryParmSupplied &&
    edition === FREE_EDITION_LABEL_KEY.toLowerCase()
  ) {
    // force standard edition if query parm supplied was set to free.
    edition = edition = STANDARD_EDITION_LABEL_KEY.toLowerCase();
  }

  // Developer Instances should always be Standard Edition
  if (queryParameters.hasOwnProperty('developer')) {
    edition = STANDARD_EDITION_LABEL_KEY.toLowerCase();
  }

  // SPICE-448: Prototype products Point A - alpha or beta editions
  if (productOrBundleData?.bundleProductKeys === ProductKey.COMPASS) {
    // BXPSU-1499 Compass GA Launch - Add support for Free and Standard
    // No Compass Premium edition so instead gets set to Standard edition
    if (edition === PREMIUM_EDITION_LABEL_KEY.toLowerCase()) {
      edition = STANDARD_EDITION_LABEL_KEY.toLowerCase();
    }
  }

  const imgSource = _.get(productOrBundleData, 'iconSrc');
  const serverDownloadContent = _.get(productOrBundleData, 'calloutRichText');
  const productContent = _.get(productOrBundleData, 'products');

  return _.map(
    (_.get(productOrBundleData, 'bundleProductKeys') || '').split(','),
    (keys) => {
      return {
        product: keys,
        edition,
        imgSource,
        serverDownloadContent,
        productContent
      };
    }
  );
};

/**
 * Given Product Key, return Product name
 */

export const getProductNameFromKey = (productKey: string): string => {
  const nameIdx = PRODUCT_NAMES.map((name: string) =>
    name.toLowerCase()
  ).findIndex((productName) => productKey.includes(productName));
  return nameIdx === -1 ? '' : PRODUCT_NAMES[nameIdx];
};

/**
 * Check for a matching productName within products
 */

export const includesProduct = ({
  products,
  productName
}: IncludesProductOptionsType): boolean => {
  return products.some(({ product: productKey }) => {
    if (typeof productKey !== 'string') {
      return false;
    }
    return getProductNameFromKey(productKey) === productName;
  });
};

export const onProductsRequireSitename = (productKeys: ProductKeysType[]) => {
  return productKeys
    .map(({ product: productKey }) => productKey)
    .map(getProductNameFromKey)
    .some((productName: string) => [BITBUCKET, OPSGENIE].includes(productName));
};
